import React, { useEffect, useMemo, useState } from "react";
import { DeviceMobile, DownloadSimple, X } from "@phosphor-icons/react";
import { Button, Card } from "./ui-kit";
import AppDownloadButtons from "./AppDownloadButtons";

function getInstallState() {
  if (typeof window === "undefined") return { platform: "desktop", installed: false };
  const ua = window.navigator.userAgent || "";
  const nativeWrapper = /NexoraCabAndroidWebView/i.test(ua);
  const standalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;
  return {
    platform: /Android/i.test(ua) ? "android" : /iPhone|iPad|iPod/i.test(ua) ? "ios" : "desktop",
    installed: standalone || nativeWrapper,
    nativeWrapper,
  };
}

export default function InstallAppPrompt({ compact = false, forceVisible = false }) {
  const initial = useMemo(getInstallState, []);
  const [installEvent, setInstallEvent] = useState(null);
  const [dismissed, setDismissed] = useState(() => !forceVisible && localStorage.getItem("nexora-install-dismissed") === "yes");
  const [state, setState] = useState(initial);
  const apkUrl = process.env.REACT_APP_ANDROID_APK_URL || "";

  useEffect(() => {
    const onPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
      setDismissed(false);
    };
    const onInstalled = () => {
      localStorage.setItem("nexora-install-dismissed", "yes");
      setState((current) => ({ ...current, installed: true }));
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (state.nativeWrapper || state.installed || (!forceVisible && dismissed)) return null;

  const close = () => {
    localStorage.setItem("nexora-install-dismissed", "yes");
    setDismissed(true);
  };

  const installPwa = async () => {
    if (!installEvent) {
      if (apkUrl && state.platform === "android") {
        close();
        window.location.href = apkUrl;
      }
      return;
    }
    installEvent.prompt();
    const choice = await installEvent.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") {
      localStorage.setItem("nexora-install-dismissed", "yes");
      setState((current) => ({ ...current, installed: true }));
    }
    setInstallEvent(null);
  };

  const isIOS = state.platform === "ios";
  const isAndroid = state.platform === "android";

  return (
    <Card className={`${compact ? "p-3" : "p-4"} relative overflow-hidden`}>
      <button
        type="button"
        onClick={close}
        className="absolute right-3 top-3 rounded-full bg-white/5 p-1.5 text-muted-foreground hover:text-foreground"
        aria-label="Fermer"
      >
        <X size={14} weight="bold" />
      </button>
      <div className="flex gap-3 pr-8">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary">
          <DeviceMobile size={24} weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-lg font-bold">Installer Nexora-Cab</div>
          <div className="mt-1 text-xs leading-5 text-muted-foreground">
            Acces rapide comme une application, en attendant la publication officielle.
          </div>
        </div>
      </div>

      {isIOS && (
        <div className="mt-3 rounded-2xl border border-primary/20 bg-primary/10 p-3 text-xs leading-5 text-muted-foreground">
          <div className="font-bold text-foreground">Installation iPhone</div>
          <ol className="mt-2 list-decimal space-y-1 pl-4">
            <li>Ouvrez ce site dans Safari.</li>
            <li>Touchez l'icone de partage en bas de l'ecran.</li>
            <li>Choisissez Ajouter a l'ecran d'accueil.</li>
            <li>Validez avec Ajouter.</li>
          </ol>
        </div>
      )}

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {isIOS ? (
          <Button onClick={close} variant="outline" className="w-full">
            J'ai compris
          </Button>
        ) : (
          <Button onClick={installPwa} className="w-full">
            <DownloadSimple weight="bold" />
            {installEvent ? "Installer l'application" : apkUrl && isAndroid ? "Telecharger APK" : "Installer Nexora"}
          </Button>
        )}
      </div>
      {isAndroid && (
        <AppDownloadButtons className="mt-3" compact onAction={close} />
      )}
    </Card>
  );
}
